// Reading data for the confluence board. The engine (confluence.ts) is pure and
// takes candles; this layer fetches and caches the candle series each card needs,
// then hands the engine what it wants. One fetch per (symbol, timeframe) is
// shared across every card that uses it, cached with a short TTL, so a board of
// many cards on the same security/timeframe costs one request.
//
// Fetch pairs mirror the shipped watchlist snapshot loader (proven range/interval
// choices); 4h is resampled from 60m exactly as loadSnapshot does.

import { Emitter, storageGet, storageSet } from "./storage.js";
import { fetchCandles, type OHLCPoint } from "./market-data.js";
import { evalReading, lenses, filtersPass, gatesPass, breakoutVerdict, relativeVolume, volatilityPercentile, type Board, type Card, type Cell, type Lens, type ReadingKey, type Verdict } from "./confluence.js";

// `range` is the LEAN window the live Watch loop fetches every refresh (enough
// bars for the current readings; gentle on the data source — D-094). `deep` is
// the much longer window the Build/Confirm BACKTEST fetches on demand, one
// symbol at a time, so multi-timeframe setups replay over years of history
// rather than the finest frame's short live window (D-119). `deep` respects
// each interval's provider cap (60m ≈ 2y, intraday ≈ 60d).
const TF_FETCH: Record<string, { range: string; interval: string; deep?: string; resampleMs?: number }> = {
  "1m": { range: "1d", interval: "1m", deep: "7d" },
  "5m": { range: "5d", interval: "5m", deep: "60d" },
  "15m": { range: "5d", interval: "15m", deep: "60d" },
  "30m": { range: "5d", interval: "30m", deep: "60d" },
  "1h": { range: "1mo", interval: "60m", deep: "2y" },
  "4h": { range: "3mo", interval: "60m", deep: "2y", resampleMs: 4 * 3_600_000 },
  "1D": { range: "6mo", interval: "1d", deep: "10y" },
  "1W": { range: "5y", interval: "1wk", deep: "max" },
  "1M": { range: "max", interval: "1mo" },
  "3M": { range: "max", interval: "3mo" },
  // No native 6-month / 1-year candle interval — resample monthly bars into
  // fixed ~6mo / ~12mo buckets (approximate; refine later).
  "6M": { range: "max", interval: "1mo", resampleMs: 6 * 2_630_016_000 },
  "1Y": { range: "max", interval: "1mo", resampleMs: 12 * 2_630_016_000 },
};

/** Fetch a symbol's full candle series for one timeframe. Uses the DEEP range
 * (years of history) so the Build/Confirm base-rate panels replay setups over a
 * long window — not the finest frame's short live window (D-119). Standalone so
 * the on-chart settings overlay pulls history without the board cache. Returns
 * [] for an unknown timeframe. */
export async function fetchSeries(sym: string, tf: string): Promise<OHLCPoint[]> {
  const map = TF_FETCH[tf];
  if (!map) return [];
  let candles = await fetchCandles(sym, map.deep ?? map.range, map.interval);
  if (map.resampleMs) candles = resampleCandles(candles, map.resampleMs);
  return candles;
}

const TTL_MS = 5 * 60_000;
const CONCURRENCY = 4;
const PERSIST_KEY = "cbCandles";
const KEEP_BARS = 80; // enough for every reading (percentile needs 60); keeps the store small
type StoredEntry = { at: number; bars: [number, number, number, number, number, number][] };

/** Resample OHLC candles into fixed-width buckets (open of first, close of last,
 * max high, min low) — used to build 4h bars from 60m. */
function resampleCandles(candles: OHLCPoint[], bucketMs: number): OHLCPoint[] {
  const buckets = new Map<number, OHLCPoint>();
  for (const c of candles) {
    const key = Math.floor(c.date.getTime() / bucketMs) * bucketMs;
    const b = buckets.get(key);
    if (!b) buckets.set(key, { date: new Date(key), open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume });
    else {
      b.high = Math.max(b.high, c.high);
      b.low = Math.min(b.low, c.low);
      b.close = c.close;
      b.volume += c.volume;
    }
  }
  return [...buckets.values()].sort((a, b) => a.date.getTime() - b.date.getTime());
}

// Cross-sectional / intermarket read across MANY instruments, so they need one
// common series for every card — daily. `ensure` fetches it for the whole board.
export const RELATIONAL_TF = "1D";

const closesOf = (c: OHLCPoint[]): number[] => c.map((p) => p.close);
const retN = (s: number[], n: number): number | null => (s.length > n ? s[s.length - 1]! / s[s.length - 1 - n]! - 1 : null);
const zScoreN = (s: number[], n: number): number | null => {
  const w = s.slice(-n);
  if (w.length < 2) return null;
  const m = w.reduce((a, b) => a + b, 0) / w.length;
  const sd = Math.sqrt(w.reduce((a, b) => a + (b - m) ** 2, 0) / w.length);
  return sd ? (w[w.length - 1]! - m) / sd : 0;
};
const donchN = (c: OHLCPoint[], n: number): number | null => {
  const w = c.slice(-n);
  if (w.length < 2) return null;
  const hi = Math.max(...w.map((x) => x.high));
  const lo = Math.min(...w.map((x) => x.low));
  return hi > lo ? (w[w.length - 1]!.close - lo) / (hi - lo) : 0.5;
};
const retsOf = (s: number[]): number[] => {
  const r: number[] = [];
  for (let i = 1; i < s.length; i++) r.push((s[i]! - s[i - 1]!) / s[i - 1]!);
  return r;
};
const pearson = (a: number[], b: number[]): number => {
  const n = Math.min(a.length, b.length);
  if (n < 3) return 0;
  const A = a.slice(-n), B = b.slice(-n);
  const ma = A.reduce((x, y) => x + y, 0) / n, mb = B.reduce((x, y) => x + y, 0) / n;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < n; i++) { num += (A[i]! - ma) * (B[i]! - mb); da += (A[i]! - ma) ** 2; db += (B[i]! - mb) ** 2; }
  return da && db ? num / Math.sqrt(da * db) : 0;
};
const mean = (a: number[]): number => a.reduce((x, y) => x + y, 0) / (a.length || 1);
// Market-neutral residuals: regress a return series on the field index and return
// what's left (its idiosyncratic move), so correlation reflects the specific link
// rather than "everything rides the market".
const residuals = (r: number[], idx: number[]): number[] => {
  const n = Math.min(r.length, idx.length);
  const R = r.slice(-n), I = idx.slice(-n);
  const mi = mean(I), mr = mean(R);
  let c = 0, v = 0;
  for (let i = 0; i < n; i++) { c += (I[i]! - mi) * (R[i]! - mr); v += (I[i]! - mi) ** 2; }
  const beta = v ? c / v : 0;
  return R.map((x, i) => x - (mr + beta * (I[i]! - mi)));
};

/** The four ways the cross-sectional lens ranks an instrument across the field.
 * Exposed so the settings UI can offer them as toggles with plain descriptions
 * (D-096: configurable + self-explaining). `id` is the persisted key; `label` is the
 * short bar caption on the card. */
export const CROSS_MEASURES: Array<{ id: string; label: string; name: string; desc: string }> = [
  { id: "ret", label: "Ret", name: "Return", desc: "Plain price change over the lookback." },
  { id: "mom", label: "Mom", name: "Momentum", desc: "Change over a longer (3×) window." },
  { id: "z", label: "Z", name: "Z-score", desc: "How stretched it is vs its own recent mean." },
  { id: "rng", label: "Rng", name: "Range", desc: "Where it sits in its high–low channel." },
];
export const CROSS_MEASURE_IDS = CROSS_MEASURES.map((m) => m.id);

/** Per-card tuning for the two relational categories (the pool is the watchlist and
 * the anchor is the card, so those aren't settings — only these are). */
export interface RelSettings { xLookback: number; xMeasures: string[]; iWindow: number; iMode: "raw" | "resid"; iMax: number; iMinAbs: number; }
export const DEFAULT_REL: RelSettings = { xLookback: 20, xMeasures: [...CROSS_MEASURE_IDS], iWindow: 40, iMode: "raw", iMax: 8, iMinAbs: 0 };

interface Entry {
  candles: OHLCPoint[] | null;
  at: number; // last successful fetch
  error?: boolean;
}

export class ConfluenceData {
  private cache = new Map<string, Entry>();
  private inflight = new Set<string>();
  readonly changed = new Emitter();

  private key(sym: string, tf: string): string {
    return `${sym}|${tf}`;
  }

  /** Load the persisted candle cache so a reopen shows the last readings instantly
   * (mirrors how the old watchlist persisted its snapshots). Stale entries still
   * display immediately; `ensure` refreshes them in the background. */
  async load(): Promise<void> {
    const stored = await storageGet<Record<string, StoredEntry>>(PERSIST_KEY, {});
    for (const [k, e] of Object.entries(stored)) {
      const candles = e.bars.map((b) => ({ date: new Date(b[0]), open: b[1], high: b[2], low: b[3], close: b[4], volume: b[5] }));
      this.cache.set(k, { candles, at: e.at });
    }
    if (Object.keys(stored).length) this.changed.emit();
  }

  private persist(): void {
    const out: Record<string, StoredEntry> = {};
    for (const [k, e] of this.cache) {
      if (!e.candles) continue;
      out[k] = { at: e.at, bars: e.candles.slice(-KEEP_BARS).map((c) => [c.date.getTime(), c.open, c.high, c.low, c.close, c.volume]) };
    }
    void storageSet(PERSIST_KEY, out);
  }

  /** Cells for one card, from whatever candles are currently cached. Uncached
   * lenses return a null cell (the UI shows them as loading). */
  cellsFor(card: Card): Cell[] {
    return lenses(card).map((lens: Lens) => {
      const entry = this.cache.get(this.key(card.sym, lens.tf));
      if (!entry || entry.candles == null) return { v: null, ends: ["—", "—"] };
      return evalReading(lens.reading, entry.candles, { period: lens.period });
    });
  }

  /** One reading's cell for a (symbol, timeframe) from the cached candles — lets a
   * category show readings other than the card's configured ones (same candles). */
  cellForReading(sym: string, reading: ReadingKey, tf: string, opts?: { lookback?: number; period?: number }): Cell {
    const entry = this.cache.get(this.key(sym, tf));
    if (!entry || entry.candles == null) return { v: null, ends: ["—", "—"] };
    return evalReading(reading, entry.candles, opts);
  }

  candlesFor(sym: string, tf: string): OHLCPoint[] | null {
    return this.cache.get(this.key(sym, tf))?.candles ?? null;
  }

  /** The breakout engine's live verdict for a card (D-115): the channel structure on the
   * breakout timeframe plus the confirm reading on its own. null while the structure
   * candles aren't cached yet. */
  breakoutVerdictFor(card: Card): Verdict {
    const p = card.breakout;
    if (!p) return null;
    const candles = this.candlesFor(card.sym, p.tf);
    if (!candles) return null;
    const confirmVal = p.confirm ? this.cellForReading(card.sym, p.confirm.reading, p.confirm.tf).v : null;
    return breakoutVerdict(candles, p, confirmVal);
  }

  /** Do a card's context gates pass? Two kinds: the volume/volatility gates (evaluated
   * on the card's first timeframe) and the reading gates (D-115, each on its own
   * timeframe). No gates, or data not yet cached → true (never suppress on missing
   * data). */
  filtersPass(card: Card): boolean {
    const f = card.filters;
    if (!f) return true;
    const hasVV = (f.volMin != null && f.volMin > 0) || (f.volatilityMin != null && f.volatilityMin > 0);
    if (hasVV) {
      const tf = card.cols[0]?.tf ?? "1D";
      const candles = this.candlesFor(card.sym, tf);
      if (candles && !filtersPass(f, { relVol: relativeVolume(candles), volatilityPct: volatilityPercentile(candles) })) return false;
    }
    if (f.gates && f.gates.length) {
      const vals = f.gates.map((g) => this.cellForReading(card.sym, g.reading, g.tf, { lookback: g.lookback, period: g.period }).v);
      if (!gatesPass(f.gates, vals)) return false;
    }
    return true;
  }

  /** Cross-sectional bars for one card: for each measure, where this instrument
   * RANKS across the whole board (0 = bottom of the field, 1 = top). */
  crossBars(board: Board, sym: string, tf: string, lookback = 20, selected?: string[]): Array<{ label: string; cell: Cell }> {
    const syms = [...new Set(board.map((c) => c.sym))];
    const cndl = new Map<string, OHLCPoint[]>();
    for (const s of syms) { const c = this.candlesFor(s, tf); if (c) cndl.set(s, c); }
    const lb = Math.max(2, Math.round(lookback));
    const momLb = Math.min(lb * 3, 60);
    const fns: Record<string, (c: OHLCPoint[]) => number | null> = {
      ret: (c) => retN(closesOf(c), lb),
      mom: (c) => retN(closesOf(c), momLb),
      z: (c) => zScoreN(closesOf(c), lb),
      rng: (c) => donchN(c, lb),
    };
    // Show the picked measures (default all); an empty pick falls back to all so a
    // card never renders zero bars.
    const pick = selected && selected.length ? CROSS_MEASURES.filter((m) => selected.includes(m.id)) : CROSS_MEASURES;
    const measures = pick.map((m) => ({ label: m.label, fn: fns[m.id]! }));
    return measures.map((m) => {
      const mine = cndl.has(sym) ? m.fn(cndl.get(sym)!) : null;
      const field = syms.map((s) => (cndl.has(s) ? m.fn(cndl.get(s)!) : null)).filter((v): v is number => v != null);
      if (mine == null || field.length < 2) return { label: m.label, cell: { v: null, ends: ["low", "high"] } };
      const pct = field.filter((v) => v <= mine).length / field.length;
      return { label: m.label, cell: { v: pct, ends: ["low", "high"] } };
    });
  }

  /** Intermarket bars for one card: its correlation (as anchor) to the OTHER
   * instruments on the board over `window` daily returns, capped at `maxNames`
   * strongest links. "resid" mode strips the field's move first (market-neutral). */
  interBars(board: Board, sym: string, tf: string, opts?: { window?: number; mode?: "raw" | "resid"; maxNames?: number; minAbs?: number }): Array<{ label: string; cell: Cell }> {
    const window = Math.max(3, Math.round(opts?.window ?? 40));
    const mode = opts?.mode ?? "raw";
    const maxNames = Math.max(1, Math.round(opts?.maxNames ?? 8));
    const minAbs = Math.max(0, Math.min(1, opts?.minAbs ?? 0));
    const syms = [...new Set(board.map((c) => c.sym))];
    const rets = new Map<string, number[]>();
    for (const s of syms) { const c = this.candlesFor(s, tf); if (c) rets.set(s, retsOf(closesOf(c))); }
    if (!rets.has(sym)) return [];
    let series = rets;
    if (mode === "resid" && rets.size > 1) {
      const all = [...rets.values()];
      const T = Math.min(...all.map((r) => r.length));
      const idx: number[] = [];
      for (let t = 0; t < T; t++) { let s = 0; for (const r of all) s += r[r.length - T + t]!; idx.push(s / all.length); }
      const resid = new Map<string, number[]>();
      for (const [s, r] of rets) resid.set(s, residuals(r, idx));
      series = resid;
    }
    const aR = series.get(sym)!.slice(-window);
    const scored = syms.filter((s) => s !== sym).map((o) => {
      const or = series.get(o);
      return { o, c: or ? pearson(aR, or.slice(-window)) : null };
    });
    scored.sort((a, b) => Math.abs(b.c ?? 0) - Math.abs(a.c ?? 0));
    // Drop links weaker than the gate (|r| < minAbs) so only meaningful correlations
    // show; minAbs 0 keeps every name (up to maxNames).
    const strong = minAbs > 0 ? scored.filter((s) => s.c != null && Math.abs(s.c) >= minAbs) : scored;
    return strong.slice(0, maxNames).map(({ o, c }) => {
      const label = o.replace(/[=^-].*$/, "").slice(0, 4) || o.slice(0, 4);
      return { label, cell: { v: c == null ? null : (c + 1) / 2, ends: ["opp", "tog"] } };
    });
  }

  /** Divergence (intermarket): find the security's most-correlated partner on the
   * board over `window` returns, then the z-score of the current spread (log A − log B)
   * against that window. A large |z| means the pair has pulled apart from its usual
   * link. Returns null with no partner / not enough data (D-114 increment 3b). */
  divergence(board: Board, sym: string, tf: string, opts?: { window?: number }): { partner: string; z: number | null; corr: number } | null {
    const window = Math.max(5, Math.round(opts?.window ?? 40));
    const syms = [...new Set(board.map((c) => c.sym))];
    const mine = this.candlesFor(sym, tf);
    if (!mine) return null;
    const rets = new Map<string, number[]>();
    for (const s of syms) { const c = this.candlesFor(s, tf); if (c) rets.set(s, retsOf(closesOf(c))); }
    const aR = rets.get(sym);
    if (!aR) return null;
    const aWin = aR.slice(-window);
    let best: { s: string; corr: number } | null = null;
    for (const s of syms) {
      if (s === sym) continue;
      const or = rets.get(s);
      if (!or) continue;
      const c = pearson(aWin, or.slice(-window));
      if (!Number.isFinite(c)) continue;
      if (!best || Math.abs(c) > Math.abs(best.corr)) best = { s, corr: c };
    }
    if (!best) return null;
    const bC = this.candlesFor(best.s, tf);
    if (!bC) return { partner: best.s, z: null, corr: best.corr };
    const aClose = closesOf(mine), bClose = closesOf(bC);
    const n = Math.min(aClose.length, bClose.length);
    const spread: number[] = [];
    for (let i = Math.max(0, n - window); i < n; i++) {
      const a = aClose[i]!, b = bClose[i]!;
      if (a > 0 && b > 0) spread.push(Math.log(a) - Math.log(b));
    }
    if (spread.length < 5) return { partner: best.s, z: null, corr: best.corr };
    const mean = spread.reduce((x, y) => x + y, 0) / spread.length;
    const sd = Math.sqrt(spread.reduce((x, y) => x + (y - mean) ** 2, 0) / spread.length);
    const z = sd > 0 ? (spread[spread.length - 1]! - mean) / sd : null;
    return { partner: best.s, z, corr: best.corr };
  }

  // "Fresh" = attempted within the TTL, regardless of success — so a failed or
  // empty fetch is NOT retried on every render (which would loop: fetch → emit →
  // render → fetch …, rebuilding the DOM and swallowing every click).
  private isFresh(entry: Entry | undefined): entry is Entry {
    return entry != null && Date.now() - entry.at < TTL_MS;
  }

  /** Fetch any (symbol, timeframe) a board needs that isn't already fresh.
   * Deduplicated, capped concurrency; emits `changed` as data lands. */
  async ensure(board: Board, opts?: { prioritySyms?: Set<string>; priorityMaxAgeMs?: number }): Promise<void> {
    const wanted = new Set<string>();
    for (const card of board) for (const c of card.cols) {
      if (TF_FETCH[c.tf]) wanted.add(this.key(card.sym, c.tf));
    }
    // Reading-gate timeframes are needed too, so a gated setup (e.g. Dip's Trend 1D)
    // can be evaluated live (D-115).
    for (const card of board) for (const g of card.filters?.gates ?? []) {
      if (TF_FETCH[g.tf]) wanted.add(this.key(card.sym, g.tf));
    }
    // Breakout engine: its channel timeframe + the confirm reading's timeframe (D-115).
    for (const card of board) if (card.breakout) {
      if (TF_FETCH[card.breakout.tf]) wanted.add(this.key(card.sym, card.breakout.tf));
      const cf = card.breakout.confirm;
      if (cf && TF_FETCH[cf.tf]) wanted.add(this.key(card.sym, cf.tf));
    }
    // Everyone also gets the daily series, so the relational categories always
    // have one common timeframe to rank / correlate across.
    for (const card of board) wanted.add(this.key(card.sym, RELATIONAL_TF));
    // Priority (expanded) cards refresh on a shorter clock and go to the front of
    // the queue, so the info you're looking at updates first and often; closed
    // cards ride the normal 5-min TTL to stay gentle on the data source (D-094).
    const prio = opts?.prioritySyms;
    const prioAge = opts?.priorityMaxAgeMs ?? TTL_MS;
    const symOf = (k: string): string => k.slice(0, k.lastIndexOf("|"));
    const freshKey = (k: string): boolean => {
      const e = this.cache.get(k);
      if (e == null) return false;
      const ttl = prio && prio.has(symOf(k)) ? prioAge : TTL_MS;
      return Date.now() - e.at < ttl;
    };
    const todo = [...wanted].filter((k) => !freshKey(k) && !this.inflight.has(k));
    if (prio) todo.sort((a, b) => (prio.has(symOf(a)) ? 0 : 1) - (prio.has(symOf(b)) ? 0 : 1));
    if (!todo.length) return;

    let cursor = 0;
    const worker = async () => {
      while (cursor < todo.length) {
        const k = todo[cursor++]!;
        this.inflight.add(k);
        const sep = k.lastIndexOf("|");
        const sym = k.slice(0, sep);
        const tf = k.slice(sep + 1);
        const map = TF_FETCH[tf]!;
        try {
          let candles = await fetchCandles(sym, map.range, map.interval);
          if (map.resampleMs) candles = resampleCandles(candles, map.resampleMs);
          this.cache.set(k, { candles, at: Date.now() });
        } catch {
          const prev = this.cache.get(k);
          this.cache.set(k, { candles: prev?.candles ?? null, at: Date.now(), error: true });
        } finally {
          this.inflight.delete(k);
        }
        this.changed.emit();
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, todo.length) }, worker));
    this.persist();
  }

  /** Drop cached series no board card needs any more (keeps memory bounded). */
  prune(board: Board): void {
    const keep = new Set<string>();
    for (const card of board) for (const c of card.cols) keep.add(this.key(card.sym, c.tf));
    for (const card of board) for (const g of card.filters?.gates ?? []) keep.add(this.key(card.sym, g.tf));
    for (const card of board) if (card.breakout) {
      keep.add(this.key(card.sym, card.breakout.tf));
      if (card.breakout.confirm) keep.add(this.key(card.sym, card.breakout.confirm.tf));
    }
    for (const card of board) keep.add(this.key(card.sym, RELATIONAL_TF));
    for (const k of [...this.cache.keys()]) if (!keep.has(k)) this.cache.delete(k);
  }
}

export const confluenceData = new ConfluenceData();
